/**
 * Loads the wall's summaries into the frames store: the Pinned row (one
 * page) and this window's Everything feed, 40 cards a page. A page that
 * lands after the frames were replaced (a project switch, a canvas load, a
 * mirror snapshot) is dropped; the feed then starts over from its first page.
 */
import { listFrameSummaries } from "@/api/frame-listing";
import { ingestSummaries } from "@/lib/frame-ingest";
import { useFramesStore } from "@/stores/frames-store";
import { useWallFeedStore } from "@/stores/wall-feed-store";

export const WALL_PAGE_SIZE = 40;

function currentGeneration(): number {
  return useFramesStore.getState().generation;
}

/** The Pinned row request in flight, per project and generation. */
let toolsInFlight: { key: string; done: Promise<void> } | null = null;

/**
 * The Pinned row: task board, memory and pins. A
 * second call for the same project and generation joins the one in flight.
 */
export function loadWallTools(
  projectId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<void> {
  const generation = currentGeneration();
  const key = `${projectId}|${generation}`;
  if (toolsInFlight?.key === key) return toolsInFlight.done;
  const done = (async () => {
    try {
      const page = await listFrameSummaries(projectId, {
        view: "tools",
        signal,
      });
      if (currentGeneration() !== generation) return;
      ingestSummaries(page.items, projectId);
      useWallFeedStore.getState().markToolsLoaded(projectId, generation);
    } catch (error) {
      // A failed Pinned page settles the row: the wall shows without it.
      if (currentGeneration() === generation) {
        useWallFeedStore.getState().markToolsFailed(projectId, generation);
      }
      throw error;
    }
  })().finally(() => {
    if (toolsInFlight?.done === done) toolsInFlight = null;
  });
  toolsInFlight = { key, done };
  return done;
}

/** Start this window's feed over; the next page is the first. */
export function resetWallFeed(projectId: string): void {
  useWallFeedStore.getState().reset(projectId, currentGeneration());
}

/**
 * The feed's next page; the first one when the feed was built for another
 * project or generation. Does nothing while a page is loading or after the
 * last one. Rejects when the request fails (aborted included).
 */
export async function loadNextWallPage(
  projectId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<void> {
  const generation = currentGeneration();
  const stale = useWallFeedStore.getState();
  if (
    stale.projectId !== projectId ||
    stale.builtForGeneration !== generation
  ) {
    resetWallFeed(projectId);
  }
  const feed = useWallFeedStore.getState();
  if (feed.loading || (feed.pagesLoaded > 0 && !feed.hasMore)) return;
  const token = feed.beginPage();
  try {
    const page = await listFrameSummaries(projectId, {
      view: "wall",
      limit: WALL_PAGE_SIZE,
      cursor: feed.cursor ?? undefined,
      signal,
    });
    if (currentGeneration() !== generation) {
      useWallFeedStore.getState().finishPage(token, null);
      return;
    }
    ingestSummaries(page.items, projectId);
    useWallFeedStore.getState().finishPage(token, page);
  } catch (error) {
    useWallFeedStore.getState().finishPage(token, null);
    throw error;
  }
}
